package com.evoapps.evoshift.domain.model

import com.evoapps.evoshift.domain.market.Market
import com.evoapps.evoshift.domain.market.payroll.WorkplacePayrollSettings
import kotlinx.serialization.Serializable

@Serializable
data class Workplace(
    val id: String,
    val ownerId: String,
    val name: String,
    val address: String,
    /** Where it is (SH-110); null until a manager sets it. SH-106's clock-in radius is measured from here. */
    val location: GeoLocation?,
    val settings: WorkplaceSettings,
    /** SH-267: the owner's workplace group it is in (null: none); set by the server only. */
    val groupId: String? = null
) {
    /**
     * Whether [userId]'s shifts here are paid work (see WorkplacePayrollSettings.ownerShiftsUnpaid
     * and, SH-147, unpaidUserIds).
     */
    fun isPaid(userId: String): Boolean =
        !(settings.payroll.ownerShiftsUnpaid && userId == ownerId) && userId !in settings.payroll.unpaidUserIds

    /**
     * SH-147: whether [userId] working here without pay terms leaves the labour cost understated
     * (the "no pay set" warning): paid work, and not the owner, for whom working without a wage
     * is normal (they may still set pay terms to have their cost counted).
     */
    fun expectsPayTerms(userId: String): Boolean = userId != ownerId && isPaid(userId)

    /** SH-147: whether [userId] is confirmed as working here without pay (WorkplacePayrollSettings.unpaidUserIds). */
    fun worksUnpaid(userId: String): Boolean = userId in settings.payroll.unpaidUserIds

    /** SH-232: [userId]'s periods without pay here (someone only on the older list: from the start). */
    fun unpaidPeriodsOf(userId: String): List<com.evoapps.evoshift.domain.budget.UnpaidPeriod> =
        com.evoapps.evoshift.domain.budget.UnpaidWork.periodsOf(settings.payroll, userId)

    /** SH-232: whether [userId] works here without pay on [day] ("YYYY-MM-DD"; empty: today's list). */
    fun worksUnpaidOn(userId: String, day: String): Boolean =
        if (day.length < 10) worksUnpaid(userId) else com.evoapps.evoshift.domain.budget.UnpaidWork.covers(unpaidPeriodsOf(userId), day)

    /**
     * SH-232: whether [userId]'s shift on [day] ("YYYY-MM-DD", a shift's date) is paid work here:
     * the periods without pay that cover the day, and an unpaid owner always.
     */
    fun isPaid(userId: String, day: String): Boolean =
        !(settings.payroll.ownerShiftsUnpaid && userId == ownerId) && !worksUnpaidOn(userId, day)

    /** SH-232: whether [userId] has paid work here on at least one day of [month] ("YYYY-MM"). */
    fun isPaidIn(userId: String, month: String): Boolean =
        !(settings.payroll.ownerShiftsUnpaid && userId == ownerId) &&
            com.evoapps.evoshift.domain.budget.UnpaidWork.paidIn(unpaidPeriodsOf(userId), month)
}

@Serializable
data class WorkplaceSettings(
    val timezone: String,
    val currency: String,
    val firstDayOfWeek: Int,
    val availableRoles: List<String>,
    val workplaceSkills: List<String>,
    val shiftSwapPolicy: SwapPolicy, // A, B, C, D
    val contractTypes: List<ContractType> = emptyList(),
    val defaultWeeklySkeleton: Map<Int, String> = emptyMap(),
    val definedAvailabilitySlots: List<AvailabilitySlot>,
    /** Whose labour law and taxes apply (see PayrollRules). */
    val market: Market = Market.POLAND,
    val payroll: WorkplacePayrollSettings = WorkplacePayrollSettings(),
    /**
     * Minutes after a shift's planned start without a clock-in before the managers are told
     * (functions/noShow.js, SH-73); 0 turns the alerts off.
     */
    val noShowAlertMinutes: Int = DEFAULT_NO_SHOW_ALERT_MINUTES,
    /**
     * Minutes after a shift's end with its closing checklist not done before the person and the
     * managers get a push (functions/checklists.js, SH-121); 0 turns the reminders off.
     */
    val checklistAlertMinutes: Int = DEFAULT_CHECKLIST_ALERT_MINUTES,
    /**
     * Assigning someone whose medical exam or OHS training has expired (SH-47): false warns and
     * offers "assign anyway", true refuses (art. 229 §4 KP: no admission to work without a valid
     * medical certificate).
     */
    val blockExpiredStaffChecks: Boolean = false,
    /**
     * SH-133: the 11-hour daily and 35-hour weekly rest (Kodeks pracy art. 132–133) for every
     * contract, as the workplace's own policy (the owner's rule 8). The law binds only employment
     * contracts; off, the roster warns and auto-assign plans the rest for them only.
     */
    val restRulesForAllContracts: Boolean = true,
    /**
     * Which Sundays (and public holidays) the workplace is open on (SH-55); the skeleton skips the
     * days it closes and the roster and the daily log mark them.
     */
    val sundayPolicy: SundayPolicy = SundayPolicy.OPEN,
    /** SH-104: positions ([availableRoles]) whose holders lead shifts (see ShiftLeadRule). */
    val leadRoles: List<String> = emptyList(),
    /** SH-104: skills ([workplaceSkills]) whose holders lead shifts (see ShiftLeadRule). */
    val leadSkills: List<String> = emptyList(),
    /** SH-106: clocking in and out only near the workplace ([Workplace.location]); off by default. */
    val geofence: GeofenceSettings = GeofenceSettings(),
    /** SH-116: paid and unpaid breaks and the break reminder. */
    val breakPolicy: BreakPolicy = BreakPolicy(),
    /** SH-134: flags on unusual clock times, payable time from the plan, tablet safeguards. */
    val clockReview: ClockReviewSettings = ClockReviewSettings(),
    /**
     * SH-226: weather for the workplace's location, fetched by the server (functions/weather.js,
     * docs/weather.md) and shown on the daily results, the roster headers and the dashboards.
     * Off by default; owners and managers switch it.
     */
    val weatherEnabled: Boolean = false,
    /**
     * SH-227: the automatic special days on the roster and the daily results: the winter break of
     * the workplace's voivodeship, the summer school holidays and the long weekends. On by
     * default; the events typed in show either way.
     */
    val specialDaysEnabled: Boolean = true,
    /** SH-227: the voivodeship the owner chose; null: read from the address (VoivodeshipFinder). */
    val voivodeship: Voivodeship? = null,
    /** SH-223: the numbers of the request types where the law sets one (settings.requests). */
    val requests: RequestSettings = RequestSettings(),
    /**
     * SH-239: "Made with EVoShift" on the e-mailed rosters and their PDF. On by default; turning it
     * off counts only on a paid plan ([MadeWithFooter]).
     */
    val showEvoShiftFooter: Boolean = true,
    /** SH-257: whether the team enters their own working hours ("Pracownicy wpisują swoje godziny"). */
    val workHours: WorkHoursSettings = WorkHoursSettings(),
    /** SH-258: how the printed reports look (the working-time record's columns, hours and rounding). */
    val reports: ReportSettings = ReportSettings(),
    /** SH-255: what employees see of their colleagues' shifts; owners and managers choose it. */
    val rosterVisibility: RosterVisibility = RosterVisibility.FULL,
    /**
     * SH-255: someone is hidden in the roster for the others (who, only the managers read). Kept
     * by the setRosterHidden function; the app never writes it.
     */
    val rosterHasHiddenPeople: Boolean = false
) {
    companion object {
        const val DEFAULT_NO_SHOW_ALERT_MINUTES = 15
        /** The choices offered in the workplace settings; 0 = off. */
        val NO_SHOW_ALERT_CHOICES = listOf(0, 5, 10, 15, 20, 30, 45, 60)
        const val DEFAULT_CHECKLIST_ALERT_MINUTES = 30
        /** The choices offered in the workplace settings; 0 = off (functions/checklists.js MAX_MINUTES). */
        val CHECKLIST_ALERT_CHOICES = listOf(0, 15, 30, 45, 60, 90, 120)
    }
}

/**
 * Sunday trading (SH-55). In Poland the Act on restricting trade on Sundays and public holidays
 * bans trade on most Sundays and on every public holiday; gastronomy, bakeries, petrol stations,
 * pharmacies and other categories are exempt (art. 6), so cafés and restaurants usually stay [OPEN].
 */
@Serializable
enum class SundayPolicy {
    /** Open every Sunday and public holiday (exempt businesses: cafés, restaurants, ...). */
    OPEN,
    /** Open only on the statutory trading Sundays; closed on public holidays (retail under the Act). */
    TRADING_SUNDAYS,
    /** Never open on Sundays, nor on public holidays. */
    CLOSED;

    companion object {
        /** Tolerant lookup: unknown or missing means [OPEN] (what every workplace did before SH-55). */
        fun fromName(name: String?): SundayPolicy = entries.firstOrNull { it.name == name } ?: OPEN
    }
}

// domain/model/workplace/SwapPolicy.kt
@Serializable
enum class SwapPolicy {
    A_DIRECT,
    B_APPROVAL,
    C_TRIAGE_DIRECT,
    D_TRIAGE_APPROVAL
}

@Serializable
data class ShiftTemplate(
    val id: String,
    val name: String,
    val startTime: String,
    val endTime: String,
    val role: String?,
    val skills: List<String>
)

@Serializable
data class DayTemplate(
    val id: String,
    val name: String,
    val shiftTemplateIds: List<String>
)

/** SH-232: whether [userId]'s work on [shift] is paid here, by the shift's own day. */
fun Workplace.isPaidFor(userId: String, shift: Shift): Boolean = isPaid(userId, shift.date.ifEmpty { shift.plannedTime.start })
